import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Package,
  MapPin,
  Tag,
  Calendar,
  TrendingUp,
  Truck,
  Download,
  Pencil,
  ExternalLink,
  Users,
  Sparkles,
  Camera,
} from "lucide-react";

interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  condition: string;
  conditionScore: number;
  price: number;
  originalMsrp: number;
  location: string;
  region: string;
  status: "available" | "reserved" | "sold" | "processing";
  source: "trade-in" | "buyback" | "partner";
  createdAt: string;
  description?: string;
  color?: string;
  size?: string;
  images?: string[];
  tradeInDate?: string;
  customerNotes?: string;
}

interface InventoryStats {
  totalItems: number;
  totalValue: number;
  byRegion: { region: string; count: number; value: number }[];
  byCategory: { category: string; count: number }[];
  byCondition: { condition: string; count: number }[];
}

// Generate CSV from inventory data
function generateInventoryCSV(items: InventoryItem[]): string {
  const headers = [
    "SKU",
    "Name",
    "Category",
    "Condition",
    "Condition Score",
    "Price",
    "Original MSRP",
    "Location",
    "Region",
    "Status",
    "Source",
    "Created At",
  ];
  const rows = items.map((item) =>
    [
      item.sku,
      item.name,
      item.category,
      item.condition,
      item.conditionScore,
      item.price,
      item.originalMsrp,
      item.location,
      item.region,
      item.status,
      item.source,
      item.createdAt,
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  return [headers.join(","), ...rows].join("\n");
}

export const Route = createFileRoute("/inventory")({
  component: InventoryDashboard,
});

interface ResaleListing {
  id: string;
  tenant_id: string;
  title: string;
  brand: string;
  model: string;
  category: string;
  condition: string;
  condition_score: number;
  price_cents: number;
  currency: string;
  image_url: string;
  description: string;
  source: string;
  colorway: string;
  gender: string;
  confidence: number;
  pricing_data: string;
  status: string;
  created_at: string;
  updated_at: string;
}

function InventoryDashboard() {
  const { currentTenant } = useTenant();
  const [activeTab, setActiveTab] = useState<"trade-in" | "consumer-resale">(
    "trade-in",
  );
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [resaleListings, setResaleListings] = useState<ResaleListing[]>([]);
  const [resaleLoading, setResaleLoading] = useState(false);
  const [resaleStats, setResaleStats] = useState<{
    total: number;
    totalValue: number;
    avgConfidence: number;
  } | null>(null);

  useEffect(() => {
    loadInventory();
  }, [currentTenant.id]);

  useEffect(() => {
    if (activeTab === "consumer-resale") {
      loadResaleListings();
    }
  }, [activeTab, currentTenant.id]);

  const loadResaleListings = async () => {
    setResaleLoading(true);
    try {
      const res = await tenantFetch("/api/v1/resale-listings?status=all");
      if (res.ok) {
        const data = await res.json();
        setResaleListings(data.items || []);
        setResaleStats(data.stats || null);
      } else {
        // Demo fallback
        setResaleListings(getDemoResaleListings());
        setResaleStats({ total: 3, totalValue: 27000, avgConfidence: 0.91 });
      }
    } catch {
      setResaleListings(getDemoResaleListings());
      setResaleStats({ total: 3, totalValue: 27000, avgConfidence: 0.91 });
    } finally {
      setResaleLoading(false);
    }
  };

  const loadInventory = async () => {
    setLoading(true);
    try {
      const res = await tenantFetch("/api/v1/inventory");
      if (res.ok) {
        const data = await res.json();
        setInventory(data.items || []);
        setStats(data.stats || null);
      } else {
        // Demo data for Arc'Teryx
        setInventory(getDemoInventory());
        setStats(getDemoStats());
      }
    } catch (error) {
      console.error("Failed to load inventory:", error);
      setInventory(getDemoInventory());
      setStats(getDemoStats());
    } finally {
      setLoading(false);
    }
  };

  // Filter inventory
  const filteredInventory = inventory.filter((item) => {
    if (
      search &&
      !item.name.toLowerCase().includes(search.toLowerCase()) &&
      !item.sku.toLowerCase().includes(search.toLowerCase())
    ) {
      return false;
    }
    if (categoryFilter && item.category !== categoryFilter) return false;
    if (regionFilter && item.region !== regionFilter) return false;
    if (conditionFilter && item.condition !== conditionFilter) return false;
    return true;
  });

  const categories = [...new Set(inventory.map((i) => i.category))];
  const regions = [...new Set(inventory.map((i) => i.region))];
  const conditions = [...new Set(inventory.map((i) => i.condition))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: currentTenant.color,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Circular Inventory
          </h1>
          <p className="text-sm mt-1" style={{ color: "#737373" }}>
            Unified view of trade-in and resale inventory across all regions
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b" style={{ borderColor: "#E5E5E5" }}>
        <button
          onClick={() => setActiveTab("trade-in")}
          className="px-6 py-3 text-sm font-light transition-colors relative flex items-center gap-2"
          style={{
            color: activeTab === "trade-in" ? currentTenant.color : "#737373",
            borderBottom:
              activeTab === "trade-in"
                ? `2px solid ${currentTenant.color}`
                : "2px solid transparent",
          }}
        >
          <Truck size={16} />
          Trade-In Inventory
        </button>
        <button
          onClick={() => setActiveTab("consumer-resale")}
          className="px-6 py-3 text-sm font-light transition-colors relative flex items-center gap-2"
          style={{
            color:
              activeTab === "consumer-resale" ? currentTenant.color : "#737373",
            borderBottom:
              activeTab === "consumer-resale"
                ? `2px solid ${currentTenant.color}`
                : "2px solid transparent",
          }}
        >
          <Users size={16} />
          Consumer Resale
          {(resaleStats?.total || 0) > 0 && (
            <span
              className="px-1.5 py-0.5 text-xs rounded-full text-white"
              style={{ backgroundColor: currentTenant.color }}
            >
              {resaleStats?.total || 0}
            </span>
          )}
        </button>
      </div>

      {/* Consumer Resale Tab Content */}
      {activeTab === "consumer-resale" ? (
        <ConsumerResaleView
          listings={resaleListings}
          stats={resaleStats}
          loading={resaleLoading}
          tenantColor={currentTenant.color}
          _onRefresh={loadResaleListings} // eslint-disable-line react/no-unused-prop-types
        />
      ) : (
        <>
          {/* Trade-In Tab Content */}
          <div className="flex items-center justify-end">
            <div></div>
            <button
              onClick={() => {
                // Generate CSV export of inventory
                const csv = generateInventoryCSV(inventory);
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `inventory-export-${new Date().toISOString().split("T")[0]}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="px-4 py-2 text-sm font-light text-white rounded-full hover:opacity-90 transition-opacity flex items-center gap-2"
              style={{ backgroundColor: currentTenant.color }}
            >
              <Download size={16} />
              Export Inventory
            </button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Items"
                value={stats.totalItems.toLocaleString()}
                subtext="In circular inventory"
                color={currentTenant.color}
              />
              <StatCard
                label="Total Value"
                value={`$${(stats.totalValue / 100).toLocaleString()}`}
                subtext="Resale value"
                color="#00FBA9"
              />
              <StatCard
                label="Regions"
                value={stats.byRegion.length.toString()}
                subtext="Active locations"
                color="#3B82F6"
              />
              <StatCard
                label="Categories"
                value={stats.byCategory.length.toString()}
                subtext="Product types"
                color="#8B5CF6"
              />
            </div>
          )}

          {/* Region Distribution */}
          {stats && (
            <div
              className=" p-6"
              style={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #E5E5E5",
              }}
            >
              <h2
                className="text-lg font-light mb-4"
                style={{ color: "#171717" }}
              >
                Regional Distribution
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stats.byRegion.map((region) => (
                  <div
                    key={region.region}
                    className="p-4  bg-white border"
                    style={{ borderColor: "#E5E5E5" }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-light" style={{ color: "#171717" }}>
                        {region.region}
                      </span>
                      <span
                        className="text-sm px-2 py-1 rounded"
                        style={{
                          backgroundColor: currentTenant.color + "20",
                          color: currentTenant.color,
                        }}
                      >
                        {region.count} items
                      </span>
                    </div>
                    <div
                      className="text-lg font-semibold"
                      style={{ color: "#171717" }}
                    >
                      ${(region.value / 100).toLocaleString()}
                    </div>
                    <div className="text-xs" style={{ color: "#737373" }}>
                      Inventory value
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-4 py-2  border text-sm w-64"
              style={{ borderColor: "#E5E5E5" }}
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2  border text-sm"
              style={{ borderColor: "#E5E5E5" }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-4 py-2  border text-sm"
              style={{ borderColor: "#E5E5E5" }}
            >
              <option value="">All Regions</option>
              {regions.map((reg) => (
                <option key={reg} value={reg}>
                  {reg}
                </option>
              ))}
            </select>
            <select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
              className="px-4 py-2  border text-sm"
              style={{ borderColor: "#E5E5E5" }}
            >
              <option value="">All Conditions</option>
              {conditions.map((cond) => (
                <option key={cond} value={cond}>
                  {cond}
                </option>
              ))}
            </select>
          </div>

          {/* Inventory Table */}
          <div
            className=" overflow-hidden"
            style={{ border: "1px solid #E5E5E5" }}
          >
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: "#FAFAFA" }}>
                  <th
                    className="text-left px-4 py-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Product
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Category
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Condition
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Location
                  </th>
                  <th
                    className="text-right px-4 py-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Price
                  </th>
                  <th
                    className="text-center px-4 py-3 text-xs font-light uppercase"
                    style={{ color: "#737373" }}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody
                className="bg-white divide-y"
                style={{ borderColor: "#E5E5E5" }}
              >
                {filteredInventory.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedItem(item)}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {item.images && item.images[0] ? (
                          <img
                            src={item.images[0]}
                            alt={item.name}
                            className="w-12 h-12 rounded object-cover"
                            style={{ backgroundColor: "#FAFAFA" }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded flex items-center justify-center"
                            style={{ backgroundColor: "#FAFAFA" }}
                          >
                            <Package
                              className="w-5 h-5"
                              style={{ color: "#A9A9A9" }}
                            />
                          </div>
                        )}
                        <div>
                          <div
                            className="font-light text-sm"
                            style={{ color: "#171717" }}
                          >
                            {item.name}
                          </div>
                          <div className="text-xs" style={{ color: "#737373" }}>
                            SKU: {item.sku}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className="px-4 py-4 text-sm"
                      style={{ color: "#525252" }}
                    >
                      {item.category}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <ConditionBadge
                          condition={item.condition}
                          score={item.conditionScore}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm" style={{ color: "#171717" }}>
                        {item.location}
                      </div>
                      <div className="text-xs" style={{ color: "#737373" }}>
                        {item.region}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div
                        className="text-sm font-light"
                        style={{ color: "#171717" }}
                      >
                        ${(item.price / 100).toFixed(0)}
                      </div>
                      <div className="text-xs" style={{ color: "#737373" }}>
                        {Math.round((item.price / item.originalMsrp) * 100)}% of
                        MSRP
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <StatusBadge status={item.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredInventory.length === 0 && (
              <div
                className="text-center py-12 text-sm"
                style={{ color: "#737373" }}
              >
                No inventory items found
              </div>
            )}
          </div>

          {/* Product Detail Modal */}
          {selectedItem && (
            <ProductDetailModal
              item={selectedItem}
              onClose={() => setSelectedItem(null)}
              tenantColor={currentTenant.color}
            />
          )}
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string;
  value: string;
  subtext: string;
  color: string;
}) {
  return (
    <div
      className=" p-5"
      style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
    >
      <div
        className="text-xs font-light uppercase mb-2"
        style={{ color: "#737373" }}
      >
        {label}
      </div>
      <div className="text-2xl font-semibold mb-1" style={{ color }}>
        {value}
      </div>
      <div className="text-xs" style={{ color: "#737373" }}>
        {subtext}
      </div>
    </div>
  );
}

function ConditionBadge({
  condition,
  score,
}: {
  condition: string;
  score: number;
}) {
  const colors: Record<string, { bg: string; text: string }> = {
    Excellent: { bg: "#DCFCE7", text: "#166534" },
    "Like New": { bg: "#DBEAFE", text: "#1E40AF" },
    Good: { bg: "#FEF3C7", text: "#92400E" },
    Fair: { bg: "#FEE2E2", text: "#991B1B" },
  };
  const style = colors[condition] || colors["Good"];

  return (
    <span
      className="px-2 py-1 rounded text-xs font-light"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {condition} ({score}/10)
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    available: { bg: "#DCFCE7", text: "#166534" },
    reserved: { bg: "#FEF3C7", text: "#92400E" },
    sold: { bg: "#E5E5E5", text: "#525252" },
    processing: { bg: "#DBEAFE", text: "#1E40AF" },
  };
  const style = colors[status] || colors["available"];

  return (
    <span
      className="px-2 py-1 rounded text-xs font-light capitalize"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status}
    </span>
  );
}

// Product Detail Modal Component
function ProductDetailModal({
  item,
  onClose,
  tenantColor,
}: {
  item: InventoryItem;
  onClose: () => void;
  tenantColor: string;
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = item.images || [];

  const nextImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + images.length) % images.length,
      );
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="bg-white max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: "#E5E5E5" }}
        >
          <div>
            <h2 className="text-xl font-light" style={{ color: "#171717" }}>
              {item.name}
            </h2>
            <p className="text-sm" style={{ color: "#737373" }}>
              SKU: {item.sku}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: "#737373" }} />
          </button>
        </div>

        <div className="flex flex-col md:flex-row max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* Image Gallery */}
          <div className="md:w-1/2 p-6 flex flex-col">
            <div
              className="relative aspect-square overflow-hidden"
              style={{ backgroundColor: "#FAFAFA" }}
            >
              {images.length > 0 ? (
                <>
                  <img
                    src={images[currentImageIndex]}
                    alt={`${item.name} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-md transition-colors"
                      >
                        <ChevronLeft
                          className="w-5 h-5"
                          style={{ color: "#171717" }}
                        />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-md transition-colors"
                      >
                        <ChevronRight
                          className="w-5 h-5"
                          style={{ color: "#171717" }}
                        />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className="w-2 h-2 rounded-full transition-colors"
                            style={{
                              backgroundColor:
                                idx === currentImageIndex
                                  ? tenantColor
                                  : "rgba(255,255,255,0.5)",
                            }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Package
                      className="w-16 h-16 mx-auto mb-3"
                      style={{ color: "#D1D1D1" }}
                    />
                    <p className="text-sm" style={{ color: "#A9A9A9" }}>
                      No images available
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className="flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-colors"
                    style={{
                      borderColor:
                        idx === currentImageIndex ? tenantColor : "transparent",
                    }}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div
            className="md:w-1/2 p-6 border-l"
            style={{ borderColor: "#E5E5E5" }}
          >
            {/* Price & Status */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <p
                  className="text-3xl font-light"
                  style={{ color: tenantColor }}
                >
                  ${(item.price / 100).toFixed(0)}
                </p>
                <p className="text-sm" style={{ color: "#737373" }}>
                  {Math.round((item.price / item.originalMsrp) * 100)}% of MSRP
                  (${(item.originalMsrp / 100).toFixed(0)})
                </p>
              </div>
              <StatusBadge status={item.status} />
            </div>

            {/* Condition */}
            <div className="mb-6">
              <p
                className="text-xs font-light uppercase mb-2"
                style={{ color: "#737373" }}
              >
                Condition
              </p>
              <div className="flex items-center gap-3">
                <ConditionBadge
                  condition={item.condition}
                  score={item.conditionScore}
                />
                <div
                  className="flex-1 h-2 rounded-full"
                  style={{ backgroundColor: "#E5E5E5" }}
                >
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${item.conditionScore * 10}%`,
                      backgroundColor:
                        item.conditionScore >= 8
                          ? "#16A34A"
                          : item.conditionScore >= 6
                            ? "#F59E0B"
                            : "#DC2626",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="space-y-4 mb-6">
              <DetailRow
                icon={<Tag />}
                label="Category"
                value={item.category}
              />
              {item.color && (
                <DetailRow
                  icon={
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.color.toLowerCase() }}
                    />
                  }
                  label="Color"
                  value={item.color}
                />
              )}
              {item.size && (
                <DetailRow icon={<Package />} label="Size" value={item.size} />
              )}
              <DetailRow
                icon={<MapPin />}
                label="Location"
                value={`${item.location}, ${item.region}`}
              />
              <DetailRow
                icon={<Truck />}
                label="Source"
                value={item.source.replace("-", " ")}
                capitalize
              />
              <DetailRow
                icon={<Calendar />}
                label="Added"
                value={new Date(item.createdAt).toLocaleDateString()}
              />
              {item.tradeInDate && (
                <DetailRow
                  icon={<TrendingUp />}
                  label="Trade-In Date"
                  value={new Date(item.tradeInDate).toLocaleDateString()}
                />
              )}
            </div>

            {/* Description */}
            {item.description && (
              <div className="mb-6">
                <p
                  className="text-xs font-light uppercase mb-2"
                  style={{ color: "#737373" }}
                >
                  Description
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "#525252" }}
                >
                  {item.description}
                </p>
              </div>
            )}

            {/* Customer Notes */}
            {item.customerNotes && (
              <div className="p-4" style={{ backgroundColor: "#FEF3C7" }}>
                <p
                  className="text-xs font-light uppercase mb-2"
                  style={{ color: "#92400E" }}
                >
                  Customer Notes
                </p>
                <p className="text-sm" style={{ color: "#92400E" }}>
                  {item.customerNotes}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div
              className="flex gap-3 mt-6 pt-6 border-t"
              style={{ borderColor: "#E5E5E5" }}
            >
              <button
                onClick={() => {
                  // Create marketplace listing
                  window.open(`/products/${item.id}`, "_blank");
                  onClose();
                }}
                className="flex-1 py-3 rounded-full text-sm font-light text-white transition-colors hover:opacity-90 flex items-center justify-center gap-2"
                style={{ backgroundColor: tenantColor }}
              >
                <ExternalLink size={16} />
                Create Listing
              </button>
              <button
                onClick={() => {
                  // Navigate to product edit
                  window.location.href = `/products/${item.id}`;
                }}
                className="px-4 py-3 rounded-full text-sm font-light border transition-colors hover:bg-gray-50 flex items-center justify-center gap-2"
                style={{ borderColor: "#E5E5E5", color: "#525252" }}
              >
                <Pencil size={16} />
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  icon,
  label,
  value,
  capitalize = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  capitalize?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-8 h-8 flex items-center justify-center"
        style={{ backgroundColor: "#FAFAFA" }}
      >
        <span style={{ color: "#737373" }}>{icon}</span>
      </div>
      <div>
        <p className="text-xs" style={{ color: "#737373" }}>
          {label}
        </p>
        <p
          className={`text-sm font-light ${capitalize ? "capitalize" : ""}`}
          style={{ color: "#171717" }}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

// Consumer Resale View Component
function ConsumerResaleView({
  listings,
  stats,
  loading,
  tenantColor,
}: {
  listings: ResaleListing[];
  stats: { total: number; totalValue: number; avgConfidence: number } | null;
  loading: boolean;
  tenantColor: string;
  // onRefresh intentionally unused — kept for future use
  _onRefresh?: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: tenantColor, borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Consumer Listings"
            value={stats.total.toString()}
            subtext="Submitted by customers"
            color={tenantColor}
          />
          <StatCard
            label="Total Value"
            value={`$${(stats.totalValue / 100).toLocaleString()}`}
            subtext="Listed resale value"
            color="#00FBA9"
          />
          <StatCard
            label="Avg AI Confidence"
            value={`${Math.round((stats.avgConfidence || 0) * 100)}%`}
            subtext="Product identification"
            color="#3B82F6"
          />
        </div>
      )}

      {/* Info Banner */}
      <div
        className="p-4 flex items-start gap-3"
        style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}
      >
        <Sparkles
          className="w-5 h-5 mt-0.5 flex-shrink-0"
          style={{ color: "#2563EB" }}
        />
        <div>
          <p className="text-sm font-medium" style={{ color: "#1E40AF" }}>
            AI-Powered Consumer Submissions
          </p>
          <p className="text-xs mt-1" style={{ color: "#3B82F6" }}>
            These items were submitted by customers through your brand's resale
            portal. Each item was automatically identified, priced, and
            described using Pentatonic's VLM technology.
          </p>
        </div>
      </div>

      {/* Listings Table */}
      <div className="overflow-hidden" style={{ border: "1px solid #E5E5E5" }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: "#FAFAFA" }}>
              <th
                className="text-left px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Product
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Category
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Condition
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                AI Confidence
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Price
              </th>
              <th
                className="text-center px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Source
              </th>
              <th
                className="text-center px-4 py-3 text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody
            className="bg-white divide-y"
            style={{ borderColor: "#E5E5E5" }}
          >
            {listings.map((listing) => (
              <tr
                key={listing.id}
                className="hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    {listing.image_url ? (
                      <img
                        src={listing.image_url}
                        alt={listing.title}
                        className="w-12 h-12 rounded object-cover"
                        style={{ backgroundColor: "#FAFAFA" }}
                      />
                    ) : (
                      <div
                        className="w-12 h-12 rounded flex items-center justify-center"
                        style={{ backgroundColor: "#FAFAFA" }}
                      >
                        <Camera
                          className="w-5 h-5"
                          style={{ color: "#A9A9A9" }}
                        />
                      </div>
                    )}
                    <div>
                      <div
                        className="font-light text-sm"
                        style={{ color: "#171717" }}
                      >
                        {listing.title}
                      </div>
                      <div className="text-xs" style={{ color: "#737373" }}>
                        {listing.brand} {listing.model && `• ${listing.model}`}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm" style={{ color: "#525252" }}>
                  {listing.category}
                </td>
                <td className="px-4 py-4">
                  <ConditionBadge
                    condition={listing.condition}
                    score={listing.condition_score}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Sparkles size={12} style={{ color: "#2563EB" }} />
                    <span className="text-sm" style={{ color: "#171717" }}>
                      {Math.round((listing.confidence || 0) * 100)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div
                    className="text-sm font-light"
                    style={{ color: "#171717" }}
                  >
                    ${(listing.price_cents / 100).toFixed(0)}
                  </div>
                </td>
                <td className="px-4 py-4 text-center">
                  <span
                    className="px-2 py-1 rounded text-xs font-light"
                    style={{ backgroundColor: "#EFF6FF", color: "#2563EB" }}
                  >
                    Consumer
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <StatusBadge status={listing.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {listings.length === 0 && (
          <div className="text-center py-12">
            <Users
              className="w-12 h-12 mx-auto mb-3"
              style={{ color: "#D1D1D1" }}
            />
            <p className="text-sm" style={{ color: "#737373" }}>
              No consumer resale listings yet
            </p>
            <p className="text-xs mt-1" style={{ color: "#A3A3A3" }}>
              Listings will appear here when customers submit items through your
              resale portal
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function getDemoResaleListings(): ResaleListing[] {
  return [
    {
      id: "rl_demo001",
      tenant_id: "adidas",
      title: "Adidas Ultraboost 22",
      brand: "Adidas",
      model: "Ultraboost 22",
      category: "Running Shoes",
      condition: "Good",
      condition_score: 7,
      price_cents: 9500,
      currency: "USD",
      image_url: "",
      description:
        "Authentic Adidas Ultraboost 22 in Core Black/Cloud White colorway. Good condition with minor signs of wear.",
      source: "consumer_submitted",
      colorway: "Core Black / Cloud White",
      gender: "mens",
      confidence: 0.92,
      pricing_data: '{"quick_sale":75,"fair_market":95,"premium":115}',
      status: "active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: "rl_demo002",
      tenant_id: "adidas",
      title: "Adidas Samba OG",
      brand: "Adidas",
      model: "Samba OG",
      category: "Lifestyle Sneakers",
      condition: "Like New",
      condition_score: 9,
      price_cents: 8500,
      currency: "USD",
      image_url: "",
      description:
        "Classic Adidas Samba OG in white/black colorway. Like new condition, barely worn.",
      source: "consumer_submitted",
      colorway: "White / Black / Gum",
      gender: "unisex",
      confidence: 0.96,
      pricing_data: '{"quick_sale":65,"fair_market":85,"premium":105}',
      status: "active",
      created_at: new Date(Date.now() - 3600000).toISOString(),
      updated_at: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: "rl_demo003",
      tenant_id: "adidas",
      title: "Adidas Gazelle Indoor",
      brand: "Adidas",
      model: "Gazelle Indoor",
      category: "Lifestyle Sneakers",
      condition: "Excellent",
      condition_score: 8,
      price_cents: 9000,
      currency: "USD",
      image_url: "",
      description:
        "Adidas Gazelle Indoor in Blue Fusion/Cloud White. Excellent condition with very light use.",
      source: "consumer_submitted",
      colorway: "Blue Fusion / Cloud White",
      gender: "mens",
      confidence: 0.88,
      pricing_data: '{"quick_sale":70,"fair_market":90,"premium":110}',
      status: "active",
      created_at: new Date(Date.now() - 7200000).toISOString(),
      updated_at: new Date(Date.now() - 7200000).toISOString(),
    },
  ];
}

// Demo data for Arc'Teryx - using local images from /public/images/products/arcteryx/
function getDemoInventory(): InventoryItem[] {
  return [
    {
      id: "1",
      name: "Beta AR Jacket",
      sku: "ARC-25854-BLK-M",
      category: "Shell Jackets",
      condition: "Like New",
      conditionScore: 9,
      price: 37500,
      originalMsrp: 62500,
      location: "Vancouver DC",
      region: "North America West",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-15",
      color: "Black",
      size: "M",
      description:
        "The Beta AR is an all-round GORE-TEX Pro shell for alpine and ski. Durable, versatile weather protection. The most versatile jacket in the collection.",
      images: [
        "/images/products/arcteryx/beta-ar-jacket.jpeg",
        "/images/products/arcteryx/beta-lt-jacket.jpeg",
      ],
      tradeInDate: "2024-12-14",
      customerNotes:
        "Minor scratches on zipper pull, otherwise excellent condition.",
    },
    {
      id: "2",
      name: "Atom LT Hoody",
      sku: "ARC-24477-PIL-L",
      category: "Insulated Jackets",
      condition: "Excellent",
      conditionScore: 10,
      price: 20300,
      originalMsrp: 29000,
      location: "Seattle Store",
      region: "North America West",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-14",
      color: "Pilot",
      size: "L",
      description:
        "Lightweight, breathable, insulated hoody with excellent moisture management. Ideal as a midlayer in cool, dry conditions, or as an outer layer during aerobic activities.",
      images: ["/images/products/arcteryx/atom-lt-hoody.jpeg"],
      tradeInDate: "2024-12-13",
    },
    {
      id: "3",
      name: "Alpha SV Jacket",
      sku: "ARC-29873-BLK-S",
      category: "Shell Jackets",
      condition: "Good",
      conditionScore: 7,
      price: 45000,
      originalMsrp: 90000,
      location: "Toronto DC",
      region: "North America East",
      status: "reserved",
      source: "trade-in",
      createdAt: "2024-12-12",
      color: "Black",
      size: "S",
      description:
        "The pinnacle of alpine protection. The most durable GORE-TEX Pro shell for severe alpine conditions. Built for extreme durability.",
      images: ["/images/products/arcteryx/alpha-sv-jacket.jpeg"],
      tradeInDate: "2024-12-10",
      customerNotes:
        "Some wear on cuffs and minor abrasion on shoulders from pack straps.",
    },
    {
      id: "4",
      name: "Gamma LT Pant",
      sku: "ARC-17498-BLK-32",
      category: "Pants",
      condition: "Like New",
      conditionScore: 8,
      price: 13500,
      originalMsrp: 22500,
      location: "Vancouver DC",
      region: "North America West",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-11",
      color: "Black",
      size: "32",
      description:
        "Versatile softshell hiking and trekking pants with good stretch and wind resistance.",
      images: ["/images/products/arcteryx/gamma-lt-pants.jpeg"],
      tradeInDate: "2024-12-09",
    },
    {
      id: "5",
      name: "Cerium LT Hoody",
      sku: "ARC-21254-BLK-M",
      category: "Insulated Jackets",
      condition: "Good",
      conditionScore: 7,
      price: 21250,
      originalMsrp: 42500,
      location: "London Store",
      region: "Europe",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-10",
      color: "Black",
      size: "M",
      description:
        "Lightweight and warm 850-fill down hoody designed for cold weather layering. Coreloft synthetic insulation in moisture-prone areas.",
      images: ["/images/products/arcteryx/cerium-lt-jacket.jpeg"],
      tradeInDate: "2024-12-08",
    },
    {
      id: "6",
      name: "Proton FL Hoody",
      sku: "ARC-27345-GRY-L",
      category: "Insulated Jackets",
      condition: "Excellent",
      conditionScore: 9,
      price: 22750,
      originalMsrp: 32500,
      location: "Munich Store",
      region: "Europe",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-09",
      color: "Grey",
      size: "L",
      description:
        "Highly air permeable synthetic insulated hoody for stop-and-go activities in cool conditions.",
      images: ["/images/products/arcteryx/proton-fl-hoody.jpeg"],
      tradeInDate: "2024-12-07",
    },
    {
      id: "7",
      name: "Zeta SL Jacket",
      sku: "ARC-21780-SOL-S",
      category: "Shell Jackets",
      condition: "Like New",
      conditionScore: 9,
      price: 29750,
      originalMsrp: 42500,
      location: "Portland Store",
      region: "North America West",
      status: "sold",
      source: "trade-in",
      createdAt: "2024-12-08",
      color: "Solitude",
      size: "S",
      description:
        "Superlight emergency GORE-TEX shell with minimalist design. Perfect packable emergency layer.",
      images: ["/images/products/arcteryx/zeta-sl-jacket.jpeg"],
      tradeInDate: "2024-12-05",
    },
    {
      id: "8",
      name: "Covert Cardigan",
      sku: "ARC-25816-BLK",
      category: "Fleece",
      condition: "Good",
      conditionScore: 7,
      price: 8400,
      originalMsrp: 14000,
      location: "Calgary Store",
      region: "North America West",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-07",
      color: "Black",
      size: "L",
      description:
        "Casual fleece cardigan with a refined look. Great for everyday wear and light layering.",
      images: ["/images/products/arcteryx/covert-cardigan.jpeg"],
      tradeInDate: "2024-12-04",
      customerNotes: "Light pilling on sleeves.",
    },
    {
      id: "9",
      name: "Beta LT Jacket",
      sku: "ARC-25854-FLX-L",
      category: "Shell Jackets",
      condition: "Good",
      conditionScore: 6,
      price: 31250,
      originalMsrp: 62500,
      location: "New York Store",
      region: "North America East",
      status: "processing",
      source: "trade-in",
      createdAt: "2024-12-06",
      color: "Blue",
      size: "L",
      description:
        "Lightweight and packable GORE-TEX shell for hiking and trekking. Excellent weather protection in a streamlined design.",
      images: [
        "/images/products/arcteryx/beta-lt-jacket.jpeg",
        "/images/products/arcteryx/beta-ar-jacket.jpeg",
      ],
      tradeInDate: "2024-12-03",
      customerNotes:
        "Some delamination near hood. Needs professional repair assessment.",
    },
    {
      id: "10",
      name: "Delta LT Zip Neck",
      sku: "ARC-24477-BLK-M",
      category: "Fleece",
      condition: "Excellent",
      conditionScore: 10,
      price: 8500,
      originalMsrp: 12900,
      location: "Tokyo Store",
      region: "Asia Pacific",
      status: "available",
      source: "trade-in",
      createdAt: "2024-12-05",
      color: "Black",
      size: "M",
      description:
        "Lightweight, breathable fleece zip-neck perfect for layering. Polartec fleece provides warmth without weight.",
      images: ["/images/products/arcteryx/delta-lt-zip-neck.jpeg"],
      tradeInDate: "2024-12-02",
    },
  ];
}

function getDemoStats(): InventoryStats {
  return {
    totalItems: 156,
    totalValue: 4875000, // $48,750
    byRegion: [
      { region: "North America West", count: 67, value: 2125000 },
      { region: "North America East", count: 42, value: 1350000 },
      { region: "Europe", count: 31, value: 950000 },
      { region: "Asia Pacific", count: 16, value: 450000 },
    ],
    byCategory: [
      { category: "Shell Jackets", count: 52 },
      { category: "Insulated Jackets", count: 48 },
      { category: "Pants", count: 28 },
      { category: "Packs", count: 18 },
      { category: "Accessories", count: 10 },
    ],
    byCondition: [
      { condition: "Excellent", count: 34 },
      { condition: "Like New", count: 56 },
      { condition: "Good", count: 48 },
      { condition: "Fair", count: 18 },
    ],
  };
}
